import { Inject, Injectable, NgZone } from '@angular/core';
import { IUser } from '../models/user.model';
import { ILoginCredentials, IRegisterData } from '../models/credentials.model';
import {
  Auth,
  User,
  applyActionCode,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import {
  Firestore,
  collection,
  doc,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  collectionData$,
  docData$,
  resettableReplay,
} from '../utils/FirestoreRx';
import { StringUtils } from '../utils/StringUtils';
import {
  BehaviorSubject,
  filter,
  firstValueFrom,
  Observable,
  take,
} from 'rxjs';
import { EmailNotVerifiedError } from '../errors/EmailNotVerifiedError';
import { RegisterNotAllFieldsFilledInError } from '../errors/RegisterNotAllFieldsFilledInError';
import { Router } from '@angular/router';
import { ChipService } from './chip.service';
import { ChipnumberInvalidError } from '../errors/ChipnumberInvalidError';
import { AUTH_STATE, FIREBASE_AUTH, FIRESTORE } from '../firebase.providers';
import { environment } from '../../environments/environment';

/** The only project ids the admin auto-login may ever fire on. `reserve-it-cee02` is
 *  production and is deliberately absent; `demo-*` ids exist only inside an emulator. */
const AUTO_LOGIN_PROJECTS = new Set(['reserve-it-test', 'demo-reserveit']);

/** The address the visitor actually asked for, read when this module is first evaluated.
 *  That is during bootstrap, before the router has had a chance to send a logged-out
 *  visitor to /login; reading it inside the service is too late, because the service is
 *  constructed after that redirect and would only ever see /login. */
const REQUESTED_URL =
  typeof location === 'undefined' ? '/' : location.pathname + location.search;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userSubject = new BehaviorSubject<IUser | null>(null);
  private userLoggedInSubject = new BehaviorSubject<boolean>(false);
  private userEmailVerifiedSubject = new BehaviorSubject<boolean>(false);
  private userIsAdminSubject = new BehaviorSubject<boolean>(false);
  private navReadySubject = new BehaviorSubject<boolean>(false);

  user$: Observable<IUser | null> = this.userSubject.asObservable();
  userLoggedIn$: Observable<boolean> = this.userLoggedInSubject.asObservable();
  userEmailVerified$: Observable<boolean> =
    this.userEmailVerifiedSubject.asObservable();
  userIsAdmin$: Observable<boolean> = this.userIsAdminSubject.asObservable();

  /** Flips true once the facts the navigation is built from are settled: the resolved
   *  auth state, the admin claim on the token, and, where the admin auto-login runs, its
   *  outcome. Deliberately not the user document, which is a Firestore round trip later
   *  and cannot change how many entries the menu has.
   *
   *  Without it the bar renders Inloggen/Aanmelden on every cold load and swaps to five
   *  or nine entries a moment later, which is the flicker members see on arrival. */
  navReady$: Observable<boolean> = this.navReadySubject.asObservable();

  constructor(
    private chipService: ChipService,
    @Inject(AUTH_STATE) authState: Observable<User | null>,
    @Inject(FIREBASE_AUTH) private auth: Auth,
    @Inject(FIRESTORE) private db: Firestore,
    private router: Router,
    private zone: NgZone,
  ) {
    // The stream rather than the Auth object: this only needs to know who is signed in,
    // and AUTH_STATE already re-enters the Angular zone (utils/AuthRx), which these
    // subjects require because they drive templates. The imperative sign-in/out calls
    // below are the only reason `auth` is still injected.
    authState.subscribe((user) => {
      {
        if (user === null) {
          this.userSubject.next(null);
          this.userLoggedInSubject.next(false);
          this.userIsAdminSubject.next(false);
          this.userEmailVerifiedSubject.next(false);
          // Nobody is signed in, but on the test site the auto-login below is about to
          // make that untrue: resolving here would render a guest menu and swap it a
          // moment later, which is the flicker this flag exists to prevent.
          if (!this.autoLoginOnTestSite()) {
            this.navReadySubject.next(true);
          }
          return;
        }

        // The navigation only needs "is this a verified member" and "is this an admin",
        // and both are already in hand the moment auth resolves: emailVerified sits on
        // the Auth user, and `admin` is the custom claim firestore.rules checks. Waiting
        // for the user document below meant a signed-in member watched an empty menu for
        // a whole Firestore round trip after every reload.
        //
        // The document still refines both a moment later, so a doc that says isAdmin
        // without the claim behaves exactly as it did before.
        this.userEmailVerifiedSubject.next(user.emailVerified);

        user
          .getIdTokenResult()
          .then((token) =>
            this.userIsAdminSubject.next(token.claims['admin'] === true),
          )
          .catch(() => this.userIsAdminSubject.next(false))
          .finally(() => this.navReadySubject.next(true));

        // filter before take(1): a document listen that starts while the connection is
        // down raises a snapshot from cache straight away, and on a cold start that
        // cache is empty. Taking that as "the first snapshot" completed the stream and
        // detached the listener, so the user document never arrived and every screen
        // built on it -- Mijn reservaties above all -- sat on its empty state until the
        // member reloaded the page.
        docData$<IUser>(doc(this.db, 'users', user.uid), this.zone)
          .pipe(
            filter((docUser): docUser is IUser => !!docUser),
            take(1),
          )
          .subscribe({
            next: (docUser) => {
              if (!user) {
                return;
              }
              // Lowercased to match how registration stores it; without that every sign-in
              // would look like drift and write again.
              const authEmail = (user.email ?? docUser.email).toLowerCase();
              const userWithFirebaseDetails: IUser = {
                id: docUser.id,
                // Firebase Auth owns the address, so a just-confirmed change shows straight
                // away instead of after the mirror-write below lands.
                email: authEmail,
                firstName: docUser.firstName,
                lastName: docUser.lastName,
                isAdmin: docUser.isAdmin,
                creationDate: docUser.creationDate,
                emailVerified: user.emailVerified,
                displayName: user.displayName ? user.displayName : '',
                chipnumber: docUser.chipnumber,
                trial: docUser.trial,
                trialUsed: docUser.trialUsed,
              };
              this.userSubject.next(userWithFirebaseDetails);
              this.userLoggedInSubject.next(true);
              this.userEmailVerifiedSubject.next(user.emailVerified);
              this.userIsAdminSubject.next(userWithFirebaseDetails.isAdmin);

              if (
                user.emailVerified !== docUser.emailVerified ||
                user.displayName !== docUser.displayName ||
                authEmail !== docUser.email
              ) {
                // Update only the fields Firebase Auth owns; a full-doc set() would wipe any
                // other field on the user doc. The e-mail catches up here because a
                // verifyBeforeUpdateEmail link is opened without a session (/mijn-gegevens),
                // so nothing can write the new address until the member signs in again.
                updateDoc(doc(this.db, 'users', user.uid), {
                  emailVerified: user.emailVerified,
                  displayName: user.displayName ? user.displayName : '',
                  email: authEmail,
                });
              }
            },
            // The session can end while this read is still in flight: loginUser signs an
            // unverified account straight back out, and the rules then deny the
            // outstanding listen (firestore.rules users/{userId}). There is nothing to
            // recover, authState is about to emit null and clear these subjects.
            error: () => undefined,
          });
      }
    });
  }

  getLoggedInUser(): Observable<IUser | null> {
    return this.user$;
  }

  getIsUserLoggedIn(): Observable<boolean> {
    return this.userLoggedIn$;
  }

  async registerUser(user: IRegisterData) {
    // A proefles account carries no chipnumber; every other field stays required.
    if (
      !user.firstName ||
      !user.lastName ||
      !user.email ||
      !user.password ||
      (!user.trial && !user.chipnumber)
    ) {
      throw new RegisterNotAllFieldsFilledInError();
    }

    // Members register with a chip number or the signup code; trial accounts skip this.
    if (!user.trial) {
      const chipIsValid = await firstValueFrom(
        this.chipService.chipIdExists(user.chipnumber),
      );
      if (!chipIsValid) {
        throw new ChipnumberInvalidError();
      }
    }

    const userCredential = await createUserWithEmailAndPassword(
      this.auth,
      user.email,
      user.password,
    );
    await sendEmailVerification(userCredential.user);

    if (userCredential.user != null) {
      const fbUser = userCredential.user;
      await setDoc(doc(this.db, 'users', fbUser.uid), {
        id: fbUser.uid,
        firstName: user.firstName.toLowerCase(),
        lastName: user.lastName.toLowerCase(),
        email: user.email.toLowerCase(),
        isAdmin: false,
        creationDate: new Date().getTime(),
        emailVerified: fbUser.emailVerified,
        chipnumber: user.trial ? '' : user.chipnumber,
        trial: user.trial === true,
        trialUsed: false,
      });

      await updateProfile(userCredential.user, {
        displayName:
          StringUtils.toTitleCase(user.firstName) +
          ' ' +
          StringUtils.toTitleCase(user.lastName),
      });

      await signOut(this.auth);
    }
  }

  async loginUser(userData: ILoginCredentials) {
    if (userData.password) {
      const userCredential = await signInWithEmailAndPassword(
        this.auth,
        userData.email,
        userData.password,
      );
      if (userCredential.user?.emailVerified === false) {
        try {
          await sendEmailVerification(userCredential.user);
        } catch (error) {
          // Firebase throttles verification mails (auth/too-many-requests), e.g. when
          // logging in right after registration already sent one. The verify-first
          // message below must never be masked by a failed resend.
        }
        await signOut(this.auth);
        throw new EmailNotVerifiedError();
      }
    }
  }

  async logoutUser() {
    // Sign out before navigating so the guest guard lets /login render; the flag makes
    // the login screen show the Dutch logout confirmation.
    await signOut(this.auth);
    localStorage.clear();
    sessionStorage.setItem(AuthService.SKIP_AUTO_LOGIN, '1');
    await this.router.navigate(['/login', { showLoggedOutMessage: true }]);
  }

  async logoutUserWithoutRedirect() {
    sessionStorage.setItem(AuthService.SKIP_AUTO_LOGIN, '1');
    await signOut(this.auth);
  }

  private static readonly SKIP_AUTO_LOGIN = 'skip-auto-login';

  /**
   * Signs a visitor in as the seeded admin, on the TEST SITE ONLY, so the deployed test
   * build can be reviewed without typing credentials every time.
   *
   * Three separate things all have to be true: the build carries `autoLoginAdmin` (only
   * environment.test.ts and environment.local.ts do), the project id is one of the two
   * non-production ones, and the visitor has not signed out in this browser session.
   * Production has no flag to read, and the project-id check means copying the flag into
   * another environment file still does nothing.
   */
  /** Returns whether a sign-in attempt is under way, so the caller knows the
   *  logged-out state it just saw is not the final answer. */
  private autoLoginOnTestSite(): boolean {
    const creds = (
      environment as { autoLoginAdmin?: { email: string; password: string } }
    ).autoLoginAdmin;
    if (
      !creds ||
      environment.production ||
      !AUTO_LOGIN_PROJECTS.has(environment.firebase.projectId) ||
      sessionStorage.getItem(AuthService.SKIP_AUTO_LOGIN)
    ) {
      return false;
    }
    signInWithEmailAndPassword(this.auth, creds.email, creds.password)
      .then(() => {
        // The guard has already run by now. On a cold load it saw a logged-out visitor,
        // sent them to /login and left them there, signed in, looking at a login form.
        // REQUESTED_URL is the address that was actually asked for, captured at module
        // load: going back to it means a deep link still lands where it was aimed
        // instead of always dumping you on /start.
        if (this.router.url.startsWith('/login')) {
          void this.router.navigateByUrl(
            REQUESTED_URL.startsWith('/login') ? '/start' : REQUESTED_URL,
          );
        }
      })
      .catch(() => {
        // Seed not present, or the password rotated. Leave the visitor signed out; the
        // login screen still works, and the menu can settle as a guest's.
        this.navReadySubject.next(true);
      });
    return true;
  }

  async verifyEmailWithCode(code: string) {
    await applyActionCode(this.auth, code);
  }

  /** Trial → member: only succeeds with a valid chip number or the current signup code
   *  (both resolved by ChipService). The Firestore rules enforce the same gate server-side. */
  async upgradeTrialUser(chipnumber: string): Promise<void> {
    const chipIsValid =
      !!chipnumber &&
      (await firstValueFrom(this.chipService.chipIdExists(chipnumber)));
    if (!chipIsValid) {
      throw new ChipnumberInvalidError();
    }
    const current = this.userSubject.getValue();
    if (!current) {
      return;
    }
    await updateDoc(doc(this.db, 'users', current.id), {
      chipnumber,
      trial: false,
    });
    this.userSubject.next({ ...current, chipnumber, trial: false });
  }

  // Admin-only data, queried on call. Not a constructor subscription: this service is
  // root-provided, so an eager listen would run for every visitor and be denied by the
  // rules for non-admins.
  returnAllUsersFromDatabase(): Observable<IUser[]> {
    return this.allUsers$;
  }

  // One listener for the whole collection, shared. /admin subscribes twice (the
  // component constructor and `users$ | async` in its template) and /overzicht a third
  // time, so an unshared stream opened three listens and read every user document three
  // times on one page. Lazy and refcounted, so nothing listens until a view asks.
  private readonly allUsers$ = collectionData$<IUser>(
    collection(this.db, 'users'),
    this.zone,
  ).pipe(resettableReplay<IUser[]>());
}
